import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Bell, MapPin, Clock } from 'lucide-react';
import './NotificationBanner.css';

const API = process.env.REACT_APP_API_URL;
const DISMISSED_KEY = 'popmap_dismissed_notifs';
const POLL_INTERVAL = 30000; // 30s

// ─── Haversine distance (meters) ─────────────────────────────────────────────
const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Type icons ───────────────────────────────────────────────────────────────
const TYPE_META = {
    instant:   { icon: <Bell size={13} />,   label: 'Announcement', color: '#2B8EE8' },
    scheduled: { icon: <Clock size={13} />,  label: 'Update',       color: '#f59e0b' },
    location:  { icon: <MapPin size={13} />, label: 'Nearby',       color: '#10b981' },
};

// ─── Single banner card ───────────────────────────────────────────────────────
const BannerCard = ({ notification, onDismiss }) => {
    const [visible, setVisible] = useState(false);
    const meta = TYPE_META[notification.triggerType] || TYPE_META.instant;

    useEffect(() => {
        // Trigger slide-in on next tick
        const t = setTimeout(() => setVisible(true), 20);
        return () => clearTimeout(t);
    }, []);

    const handleDismiss = () => {
        setVisible(false);
        setTimeout(() => onDismiss(notification._id), 300);
    };

    return (
        <div className={`nb-card ${visible ? 'nb-card--visible' : ''}`}>
            <div className="nb-card-accent" style={{ background: meta.color }} />
            <div className="nb-card-content">
                <div className="nb-card-header">
                    <span className="nb-type-badge" style={{ color: meta.color }}>
                        {meta.icon}
                        {meta.label}
                    </span>
                    <button className="nb-dismiss-btn" onClick={handleDismiss} aria-label="Dismiss">
                        <X size={14} />
                    </button>
                </div>
                <p className="nb-title">{notification.title}</p>
                {notification.body && (
                    <p className="nb-body">{notification.body}</p>
                )}
                {notification.triggerType === 'location' && notification.zone?.markerName && (
                    <p className="nb-location-hint">
                        <MapPin size={11} /> Near {notification.zone.markerName}
                    </p>
                )}
            </div>
        </div>
    );
};

// ─── NotificationBanner ───────────────────────────────────────────────────────
// Props:
//   mapId        — MongoDB _id of the map (from mapData.id in PublicMapViewer)
//   userLocation — { lat, lng } from useMapWithLocation (nullable)
const NotificationBanner = ({ mapId, userLocation }) => {
    const [activeNotifs, setActiveNotifs] = useState([]);
    const dismissedRef = useRef(new Set());
    const pollRef = useRef(null);

    // Load dismissed IDs from sessionStorage on mount
    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(DISMISSED_KEY);
            if (stored) {
                const ids = JSON.parse(stored);
                dismissedRef.current = new Set(ids);
            }
        } catch (_) { /* ignore */ }
    }, []);

    const saveDismissed = (set) => {
        try {
            sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
        } catch (_) { /* ignore */ }
    };

    // ── Filter which notifications should actually show ──
    const shouldShow = useCallback((notif) => {
        // Already dismissed this session
        if (dismissedRef.current.has(notif._id)) return false;

        // Expired
        if (new Date(notif.expiresAt) <= new Date()) return false;

        if (notif.triggerType === 'location') {
            // Only show if we have GPS and attendee is within radius
            if (!userLocation) return false;
            if (!notif.zone?.markerLat || !notif.zone?.markerLng) return false;
            const dist = getDistance(
                userLocation.lat, userLocation.lng,
                notif.zone.markerLat, notif.zone.markerLng
            );
            return dist <= (notif.zone.radiusMeters || 10);
        }

        // instant + scheduled both show as-is (backend already filtered scheduledAt <= now)
        return true;
    }, [userLocation]);

    // ── Fetch active notifications ──
    const fetchActive = useCallback(async () => {
        if (!mapId) return;
        try {
            const res = await fetch(`${API}/api/notifications/${mapId}/active`);
            const data = await res.json();
            if (!data.success) return;

            const toShow = (data.data.notifications || []).filter(shouldShow);
            setActiveNotifs(toShow.slice(0, 3)); // max 3 stacked
        } catch (_) { /* silently fail */ }
    }, [mapId, shouldShow]);

    // ── Initial fetch + polling ──
    useEffect(() => {
        fetchActive();
        pollRef.current = setInterval(fetchActive, POLL_INTERVAL);
        return () => clearInterval(pollRef.current);
    }, [fetchActive]);

    // ── Re-check location triggers when GPS updates ──
    useEffect(() => {
        if (!userLocation) return;
        setActiveNotifs(prev => prev.filter(shouldShow));
        // Also re-fetch in case new location triggers became relevant
        fetchActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation?.lat, userLocation?.lng]);

    // ── Dismiss handler ──
    const handleDismiss = useCallback((id) => {
        dismissedRef.current.add(id);
        saveDismissed(dismissedRef.current);
        setActiveNotifs(prev => prev.filter(n => n._id !== id));
    }, []);

    if (activeNotifs.length === 0) return null;

    return (
        <div className="nb-stack">
            {activeNotifs.map(notif => (
                <BannerCard
                    key={notif._id}
                    notification={notif}
                    onDismiss={handleDismiss}
                />
            ))}
        </div>
    );
};

export default NotificationBanner;